import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { NexsetDetailDrawer } from '../NexsetDetailDrawer';
import { useDistance } from 'new/hooks/useDistance';
import { PopoverMenu, usePopoverMenu } from 'new/elements/PopoverMenu';
import { ReactComponent as EyeIcon } from 'new/components/icons/eye.svg';
import { CodeEditorMode } from 'new/elements/CodeEditorMode/CodeEditorMode';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  ADD_DATA_SOURCE_REQUEST,
  ADD_SINK_REQUEST,
  UPDATE_DATA_SET_INFO_REQUEST,
  UPDATE_SINK_INFO_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import { isUiModeCode } from 'new/utils/uiMode';

type Keys = 'view-json-config' | 'view-nexset-details' | string;

interface Props {
  nexsetId?: string;
  isDestination?: boolean;
  isTemplatized?: boolean;
  resourceConfig?: any;
  disabledActions?: Keys[];
  /** Connector id for JSON editor header chip (e.g. same value as HeaderMetaConfiguration `connection`). */
  connectorType?: string;
  /**
   * true: shows the switch mode ui button and changes the cancel behavior to exit the form
   * false: don't show the switch mode ui button and the cancel button will behave closing the modal
   */
  resourcePersisted?: boolean;

  jsonConfigOnClose?(): void;

  jsonConfigOnSave?(config: string): void;

  onSwitchUiMode?(): void;
}

enum VIEWS {
  NEXSET_DETAILS = 'view-nexset-details',
  JSON_CONFIG = 'view-json-config',
}

const NEXSET_ACTIONS = [
  {
    key: VIEWS.NEXSET_DETAILS,
    title: 'View Nexset Details',
    icon: <EyeIcon />,
  },
  {
    key: VIEWS.JSON_CONFIG,
    title: 'View JSON Config',
    icon: <EyeIcon />,
  },
];

const SOURCE_STATIC_ACTIONS = [
  {
    key: VIEWS.JSON_CONFIG,
    title: 'View JSON Config',
    icon: <EyeIcon />,
  },
];

const getConfiguration = (
  resourceConfig: Props['resourceConfig'],
  isDestination?: boolean,
  isTemplatized?: boolean,
) => {
  let config: { [x: string]: string };

  if (isDestination) {
    config = isTemplatized
      ? resourceConfig?.['template_config'] || resourceConfig?.['sink_config']
      : resourceConfig?.['sink_config'];
  } else {
    config = isTemplatized
      ? resourceConfig?.['template_config'] || resourceConfig?.['source_config']
      : resourceConfig?.['source_config'] || resourceConfig?.['api_config'];
  }

  return {
    config,
    isUiModeCode: isUiModeCode(config),
  };
};

export const ResourcePopoverMenu = (props: Props) => {
  const {
    nexsetId,
    resourceConfig = {},
    disabledActions = [],
    jsonConfigOnSave,
    jsonConfigOnClose,
    onSwitchUiMode,
    connectorType,
    resourcePersisted: resourcePersistedProp,
    isDestination,
    isTemplatized,
  } = props;

  const resourcePersisted = resourcePersistedProp ?? true;

  const topRef = useRef();
  const popoverRef = usePopoverMenu();
  const isSwitchingModeRef = useRef(false);
  const editorHeight = useDistance(topRef);
  const calculatedEditorHeight = Math.min(760, Math.max(460, (editorHeight || 700) - 260));

  const { isUiModeCode } = getConfiguration(resourceConfig, isDestination, isTemplatized);

  const canAutoOpenJson = !disabledActions.includes(VIEWS.JSON_CONFIG);

  const loading = useIsLoading(
    ADD_DATA_SOURCE_REQUEST,
    UPDATE_SOURCE_INFO_REQUEST,
    ADD_SINK_REQUEST,
    UPDATE_SINK_INFO_REQUEST,
    UPDATE_DATA_SET_INFO_REQUEST,
  );

  const unselectComponent = useCallback(() => {
    if (popoverRef.current) {
      popoverRef.current.unselectComponent();
    }
  }, []);

  const availableActions = useMemo(() => {
    const filter = (act: { key: string }) => !disabledActions.includes(act.key);
    return nexsetId ? NEXSET_ACTIONS.filter(filter) : SOURCE_STATIC_ACTIONS.filter(filter);
  }, [disabledActions]);

  useEffect(() => {
    if (isSwitchingModeRef.current) return;

    if (isUiModeCode && canAutoOpenJson) {
      popoverRef.current.selectComponent(VIEWS.JSON_CONFIG);
    }
  }, [isUiModeCode, canAutoOpenJson]);

  useEffect(() => {
    if (!isUiModeCode) {
      isSwitchingModeRef.current = false;
    }
  }, [isUiModeCode]);

  const onSave = useCallback((data: string) => jsonConfigOnSave?.(data), [jsonConfigOnSave]);

  const onClose = useCallback(() => {
    if (isUiModeCode && resourcePersisted) {
      jsonConfigOnClose?.();
      return;
    }
    unselectComponent();
  }, [isUiModeCode, jsonConfigOnClose, resourcePersisted]);

  const onSwitchMode = useCallback(() => {
    isSwitchingModeRef.current = true;
    onSwitchUiMode?.();
    unselectComponent();
  }, [onSwitchUiMode, unselectComponent]);

  if (!availableActions.length) return null;

  if (nexsetId) {
    const containerKey = isUiModeCode ? 'code' : 'view';
    return (
      <div ref={topRef} key={containerKey}>
        <PopoverMenu
          ref={popoverRef}
          actions={availableActions}
          components={{
            [VIEWS.NEXSET_DETAILS]: (
              <NexsetDetailDrawer nexsetId={nexsetId} onClose={unselectComponent} />
            ),
            [VIEWS.JSON_CONFIG]: (
              <CodeEditorMode
                open
                isLoading={loading}
                config={resourceConfig}
                cancelable={!isUiModeCode || !resourcePersisted}
                editorHeight={calculatedEditorHeight}
                onSave={onSave}
                onClose={onClose}
                onSwitchUiMode={onSwitchMode}
                connectorType={connectorType}
              />
            ),
          }}
        />
      </div>
    );
  }

  return (
    <div ref={topRef}>
      <PopoverMenu
        ref={popoverRef}
        actions={availableActions}
        components={{
          [VIEWS.JSON_CONFIG]: (
            <CodeEditorMode
              open
              isLoading={loading}
              config={resourceConfig}
              cancelable={!isUiModeCode || !resourcePersisted}
              editorHeight={calculatedEditorHeight}
              onClose={onClose}
              onSave={onSave}
              onSwitchUiMode={onSwitchMode}
              connectorType={connectorType}
            />
          ),
        }}
      />
    </div>
  );
};
